import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { ReviewCard, StarRating } from '../components/ReviewCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { isAuthenticated, axiosInstance } = useAuth();

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error('Veuillez écrire un commentaire');
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post('/reviews', { rating, comment });
      toast.success('Merci pour votre avis !');
      setComment('');
      setRating(5);
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'avis');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-4xl mx-auto py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 mb-12"
        >
          <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
            Témoignages
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-navy">
            Avis de nos clients
          </h1>
          <p className="text-navy/60 max-w-2xl mx-auto">
            Découvrez ce que pensent nos clients de notre cuisine et de notre service.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-card mb-8 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-4xl font-bold text-terracotta">{averageRating}</p>
              <div className="flex justify-center my-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    className={
                      i < Math.round(Number(averageRating))
                        ? 'fill-ocre text-ocre'
                        : 'text-stone-300'
                    }
                  />
                ))}
              </div>
              <p className="text-sm text-navy/60">{reviews.length} avis</p>
            </div>
          </div>

          {isAuthenticated && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-primary flex items-center gap-2"
              data-testid="write-review-button"
            >
              <MessageSquare size={20} />
              Écrire un avis
            </button>
          )}
        </motion.div>

        {/* Review Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-2xl p-6 shadow-card mb-8"
          >
            <h3 className="font-heading text-xl font-semibold text-navy mb-4">
              Partagez votre expérience
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Votre note
                </label>
                <StarRating rating={rating} setRating={setRating} />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Votre commentaire
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Partagez votre expérience avec nous..."
                  rows={4}
                  className="input-field w-full resize-none"
                  data-testid="review-comment-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                  data-testid="submit-review-button"
                >
                  {submitting ? 'Envoi...' : 'Publier mon avis'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="space-y-4" data-testid="reviews-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/50 h-32 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-list">
            {reviews.map((review, index) => (
              <motion.div
                key={review.review_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ReviewCard review={review} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <MessageSquare className="mx-auto text-navy/30 mb-4" size={48} />
            <p className="text-navy/60">Soyez le premier à laisser un avis !</p>
          </div>
        )}
      </div>
    </main>
  );
}
